import { useState, useEffect } from 'react';
import { adminApi } from '../../api/client';
import { Link } from 'react-router-dom';
import { Download, RefreshCw, CheckCircle2, XCircle, ExternalLink, ChevronLeft } from 'lucide-react';

// Field-by-field diff card. Shown for each staging row. Operator
// ticks the boxes for fields they want to apply, then submits.
const FIELD_LABELS = {
  name_nl: 'Public name (NL)',
  price: 'Price (incl. VAT)',
  netContent: 'Net content',
  packageLabel: 'Package label',
  usageHtml_nl: 'Usage (NL)',
  compositionHtml_nl: 'Composition (NL)',
  warningsHtml_nl: 'Warnings (NL)',
  description_nl: 'Short description (NL)',
  imageUrl: 'Main image URL',
  galleryImages: 'Gallery images',
  legacySourceUrl: 'Legacy source URL (NL)',
  legacyScrapedAt: 'Legacy scraped at',
  // FR canonical fields. Labels stay in English to match the
  // admin's mental model; the language tag in the parens
  // disambiguates.
  name_fr: 'Public name (FR)',
  usageHtml_fr: 'Usage (FR)',
  compositionHtml_fr: 'Composition (FR)',
  warningsHtml_fr: 'Warnings (FR)',
  description_fr: 'Short description (FR)',
  legacySourceUrl_fr: 'Legacy source URL (FR)',
};

const HIGH_RISK_FIELDS = new Set(['price', 'name_nl', 'name_fr']);

export default function LegacyShopImport() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [persistOnNext, setPersistOnNext] = useState(true);
  const [language, setLanguage] = useState('nl');

  const runPreview = async (persist) => {
    setBusy(true);
    try {
      const { data } = await adminApi.legacyScrapePreview({
        persistStaging: !!persist,
        language,
      });
      setResult(data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const summary = result?.discovery || {};
  const sample = result?.sample || [];

  return (
    <div data-testid="legacy-shop-import-page" className="p-8">
      <div className="flex items-center gap-3 mb-2">
        <Link to="/admin/products" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm">
          <ChevronLeft className="w-4 h-4" /> Producten
        </Link>
      </div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="font-serif text-3xl font-light text-foreground">Legacy Shop Import</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Scrape <code className="text-xs">theherborist.be/{language === 'fr' ? 'fr/produits' : 'nl/producten'}</code> for public webshop content
            and stage it for review. Nothing is published. Existing fields are preserved
            unless you explicitly select them.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-border overflow-hidden text-xs"
               data-testid="language-selector">
            {['nl', 'fr'].map(lang => (
              <button key={lang} type="button"
                data-testid={`language-btn-${lang}`}
                disabled={busy || language === lang}
                onClick={() => { setLanguage(lang); setResult(null); }}
                className={`px-3 py-1.5 font-semibold tracking-wide uppercase ${
                  language === lang
                    ? 'bg-foreground text-background'
                    : 'bg-background text-foreground hover:bg-muted'
                }`}>
                {lang}
              </button>
            ))}
          </div>
          <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground select-none">
            <input type="checkbox" data-testid="persist-toggle" checked={persistOnNext}
              onChange={e => setPersistOnNext(e.target.checked)} />
            Persist to staging
          </label>
          <button data-testid="run-preview-btn" disabled={busy}
            onClick={() => runPreview(persistOnNext)}
            className="inline-flex items-center gap-2 bg-foreground text-background rounded-full px-5 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {busy ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {busy ? 'Scraping…' : `Start ${language.toUpperCase()} preview scrape`}
          </button>
        </div>
      </div>

      {result?.error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded-lg p-3 text-sm mb-4">
          Error: {result.error}
        </div>
      )}

      {result && !result.error && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <Stat label="URLs found" value={summary.urlsFound} />
            <Stat label="Pages parsed" value={summary.pagesParsed} accent="emerald" />
            <Stat label="Matched" value={result.matched} accent="emerald" />
            <Stat label="Unmatched" value={result.unmatched} accent="amber" />
            <Stat label="Failed" value={(summary.failedUrls || []).length} accent="rose" />
          </div>

          {result.persisted && (
            <p className="text-xs text-emerald-700 mb-3">
              <CheckCircle2 className="w-3 h-3 inline mr-1" />
              {sample.length} rows upserted into <code>legacy_product_import_staging</code>.
            </p>
          )}

          {/* Bulk-apply dry-run + commit */}
          <BulkApplyPanel language={language} onCommitted={() => runPreview(true)} />

          <div className="space-y-3">
            {sample.map((row, idx) => (
              <StagingCard
                key={row.sourceUrl}
                row={row}
                onApplied={(applied, updatedRow) => {
                  setResult(r => ({
                    ...r,
                    sample: r.sample.map(s => {
                      if (s.sourceUrl !== row.sourceUrl) return s;
                      // If the link/unlink endpoint returned a fresh
                      // staging row, replace whole row (keeps match
                      // metadata in sync); otherwise just mark the
                      // field set as applied.
                      if (updatedRow) return { ...updatedRow };
                      return { ...s, _appliedFields: applied };
                    }),
                    matched: r.sample.filter(s =>
                      (s.sourceUrl === row.sourceUrl && updatedRow ? updatedRow : s).matchedProductId
                    ).length,
                    unmatched: r.sample.filter(s => !(
                      (s.sourceUrl === row.sourceUrl && updatedRow ? updatedRow : s).matchedProductId
                    )).length,
                  }));
                }}
              />
            ))}
          </div>

          {(summary.failedUrls || []).length > 0 && (
            <div className="mt-6 bg-rose-50 border border-rose-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-rose-800 mb-2">Failed URLs</p>
              <ul className="text-xs text-rose-700 font-mono space-y-1">
                {summary.failedUrls.slice(0, 12).map(f => (
                  <li key={f.url}>{f.url} — status {f.status} {f.message ? `(${f.message})` : ''}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {!result && !busy && (
        <div className="bg-muted/30 border border-dashed border-border rounded-xl p-8 text-center">
          <p className="text-sm text-muted-foreground">Click <strong>Start preview scrape</strong> to discover and parse all public product pages from the legacy shop.</p>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  const cls = {
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-800',
    amber: 'bg-amber-50 border-amber-200 text-amber-800',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
  }[accent] || 'bg-card border-border text-foreground';
  return (
    <div className={`rounded-xl border p-3 ${cls}`}>
      <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-0.5">{value ?? 0}</p>
    </div>
  );
}

// Per-language bulk-apply field sets. Canonical product schema only;
// no legacy `usage_nl`/`composition_nl`/`shortDescription_nl`. Image
// fields are deliberately NOT in the default list because admins
// upload local /api/uploads/products/*.webp from disk now — the
// legacy URL is only kept as a "reference" affordance behind an
// expander.
const BULK_FIELDS_BY_LANG = {
  nl: {
    safe: [
      'description_nl', 'usageHtml_nl', 'compositionHtml_nl',
      'warningsHtml_nl', 'netContent', 'packageLabel', 'legacySourceUrl',
    ],
    defaults: ['usageHtml_nl', 'compositionHtml_nl', 'packageLabel',
                'legacySourceUrl'],
    advancedRef: ['imageUrl', 'galleryImages'],
  },
  fr: {
    safe: [
      'description_fr', 'usageHtml_fr', 'compositionHtml_fr',
      'warningsHtml_fr', 'legacySourceUrl_fr',
    ],
    defaults: ['usageHtml_fr', 'compositionHtml_fr', 'legacySourceUrl_fr'],
    advancedRef: [],  // FR scrape never proposes image fields
  },
};

function BulkApplyPanel({ language = 'nl', onCommitted }) {
  const cfg = BULK_FIELDS_BY_LANG[language] || BULK_FIELDS_BY_LANG.nl;
  const [fields, setFields] = useState(new Set(cfg.defaults));
  const [onlyEmpty, setOnlyEmpty] = useState(true);
  const [excludeRejected, setExcludeRejected] = useState(true);
  const [showAdvancedRef, setShowAdvancedRef] = useState(false);
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const [committed, setCommitted] = useState(null);

  // Language change → clear stale plan + reset defaults to the
  // chosen language's safe defaults. Operators should never carry
  // a NL plan into an FR commit.
  useEffect(() => {
    setFields(new Set(cfg.defaults));
    setPlan(null);
    setCommitted(null);
    setShowAdvancedRef(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const toggle = (f) => setFields(prev => {
    const next = new Set(prev);
    next.has(f) ? next.delete(f) : next.add(f);
    return next;
  });

  const runDryRun = async () => {
    setBusy(true);
    setCommitted(null);
    try {
      const { data } = await adminApi.legacyStagingBulkApplyDryRun({
        fields: Array.from(fields),
        onlyMatched: true,
        onlyEmptyFields: onlyEmpty,
        excludeRejected,
        language,
      });
      setPlan(data);
    } catch (e) {
      setPlan({ error: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!plan || !plan.plan) return;
    if (!window.confirm(`Commit bulk-apply of ${Array.from(fields).join(', ')} across ${plan.rowsApplicable} matched rows (${language.toUpperCase()})?`)) return;
    setBusy(true);
    try {
      const ids = plan.plan.map(p => p.stagingId);
      const { data } = await adminApi.legacyStagingBulkApply({
        stagingIds: ids,
        fields: Array.from(fields),
        dryRun: false,
        onlyMatched: true,
        onlyEmptyFields: onlyEmpty,
        excludeRejected,
        language,
      });
      setCommitted(data);
      onCommitted?.();
    } catch (e) {
      setCommitted({ error: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const renderChip = (f) => (
    <label key={f}
      data-testid={`bulk-field-${f}`}
      className={`text-xs font-mono px-2.5 py-1 rounded-full border cursor-pointer select-none transition-colors ${
        fields.has(f)
          ? 'bg-emerald-600 text-white border-emerald-700'
          : 'bg-white text-muted-foreground border-border hover:bg-muted/40'
      }`}
      title={FIELD_LABELS[f] || f}>
      <input type="checkbox" className="hidden" checked={fields.has(f)} onChange={() => toggle(f)} />
      {FIELD_LABELS[f] || f}
    </label>
  );

  return (
    <div data-testid={`bulk-apply-panel-${language}`} className="bg-emerald-50/40 border border-emerald-200 rounded-xl p-4 mb-5">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Bulk apply (safe fields) · <span className="font-mono uppercase">{language}</span>
          </h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            Dry-run first. Only matched rows. Existing values are preserved by default.
            <strong> isActive / slug / sku / vatRate / price are server-blocked.</strong>
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <label className="inline-flex items-center gap-1.5 select-none">
            <input type="checkbox" data-testid="only-empty-toggle" checked={onlyEmpty}
              onChange={e => setOnlyEmpty(e.target.checked)} />
            Only fill empty fields
          </label>
          <label className="inline-flex items-center gap-1.5 select-none">
            <input type="checkbox" data-testid="exclude-rejected-toggle" checked={excludeRejected}
              onChange={e => setExcludeRejected(e.target.checked)} />
            Exclude rejected
          </label>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {cfg.safe.map(renderChip)}
      </div>

      {cfg.advancedRef.length > 0 && (
        <div className="mb-3 border-t border-emerald-100 pt-2">
          <button type="button"
            data-testid="bulk-advanced-toggle"
            onClick={() => setShowAdvancedRef(v => !v)}
            className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            {showAdvancedRef ? '▾' : '▸'} Legacy image reference only ({cfg.advancedRef.length})
          </button>
          {showAdvancedRef && (
            <>
              <p className="text-[11px] text-amber-700 mt-1.5 mb-2">
                Avoid bulk-applying images. Admins upload local
                <code className="mx-1">/api/uploads/products/*.webp</code>
                from disk. The legacy URL is kept here for reference only;
                applying it overwrites your uploaded image with a hot-link
                to the old shop.
              </p>
              <div className="flex flex-wrap gap-2">
                {cfg.advancedRef.map(renderChip)}
              </div>
            </>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <button data-testid="bulk-dryrun-btn" onClick={runDryRun} disabled={busy || fields.size === 0}
          className="inline-flex items-center gap-1.5 bg-foreground text-background rounded-full px-4 py-1.5 text-xs font-semibold hover:opacity-90 disabled:opacity-50">
          {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
          Preview bulk apply
        </button>
        {plan && plan.plan && !plan.error && (
          <button data-testid="bulk-commit-btn" onClick={commit} disabled={busy || plan.rowsApplicable === 0}
            className="inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-full px-4 py-1.5 text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50">
            Commit ({plan.rowsApplicable} rows)
          </button>
        )}
      </div>

      {plan?.error && (
        <p className="text-xs text-rose-700">Error: {plan.error}</p>
      )}

      {plan && !plan.error && (
        <div className="bg-white border border-border rounded-lg p-3 text-xs space-y-2">
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <Stat2 label="Total considered" value={plan.totalConsidered} />
            <Stat2 label="Applicable" value={plan.rowsApplicable} accent="emerald" />
            <Stat2 label="Skipped unmatched" value={plan.rowsSkippedUnmatched} accent="amber" />
            <Stat2 label="Skipped rejected" value={plan.rowsSkippedRejected} accent="rose" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <p className="font-semibold text-foreground mb-1">Will fill</p>
              <ul className="font-mono text-[11px] space-y-0.5">
                {Object.entries(plan.summaryFieldsFilled || {}).map(([k, n]) => (
                  <li key={k}>
                    <span className="text-emerald-700">+{n}</span> {k}
                  </li>
                ))}
                {Object.keys(plan.summaryFieldsFilled || {}).length === 0 && <li className="text-muted-foreground">— none</li>}
              </ul>
            </div>
            <div>
              <p className="font-semibold text-foreground mb-1">Skipped (non-empty / conflict)</p>
              <ul className="font-mono text-[11px] space-y-0.5">
                {Object.entries(plan.summaryFieldsConflicts || {}).map(([k, n]) => (
                  <li key={k}>
                    <span className="text-amber-700">~{n}</span> {k}
                  </li>
                ))}
                {Object.keys(plan.summaryFieldsConflicts || {}).length === 0 && <li className="text-muted-foreground">— none</li>}
              </ul>
            </div>
          </div>
          {(plan.fieldsRejected || []).length > 0 && (
            <p className="text-rose-700">
              <strong>Server-blocked fields:</strong> {plan.fieldsRejected.join(', ')}
            </p>
          )}
        </div>
      )}

      {committed && (
        <div className={`mt-3 rounded-lg p-3 text-xs ${committed.error ? 'bg-rose-50 text-rose-800 border border-rose-200' : 'bg-emerald-50 text-emerald-800 border border-emerald-200'}`}>
          {committed.error ? `Error: ${committed.error}` : (
            <>
              <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />
              Committed: <strong>{committed.appliedCount}</strong> rows updated.
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Stat2({ label, value, accent }) {
  const cls = {
    emerald: 'text-emerald-700', amber: 'text-amber-700', rose: 'text-rose-700',
  }[accent] || 'text-foreground';
  return (
    <span className="text-xs">
      <span className="text-muted-foreground">{label}:</span>{' '}
      <span className={`font-bold ${cls}`}>{value ?? 0}</span>
    </span>
  );
}

function StagingCard({ row, onApplied }) {
  const [selected, setSelected] = useState(new Set());
  const [allowPrice, setAllowPrice] = useState(false);
  const [allowOverwrite, setAllowOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkProductId, setLinkProductId] = useState('');
  const [linkNote, setLinkNote] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);

  const proposed = row.proposedUpdates || {};
  const conflicts = row.conflicts || {};
  const applied = row._appliedFields || [];
  const stagingId = row._id;
  const isManualOverride = row.matchedBy === 'manual_override';

  // Lazy-load products only when the operator opens the link
  // dropdown so unmatched rows don't pay the cost upfront.
  const ensureProducts = async () => {
    if (allProducts.length > 0) return;
    try {
      const { data } = await adminApi.listProducts();
      setAllProducts(data || []);
    } catch (e) {
      setFeedback({ ok: false, msg: e?.response?.data?.detail || e.message });
    }
  };

  const filteredProducts = (() => {
    const q = productSearch.trim().toLowerCase();
    const list = allProducts.slice().sort((a, b) =>
      (a.name_nl || '').localeCompare(b.name_nl || '', 'nl', { sensitivity: 'base' }));
    if (!q) return list.slice(0, 50);
    return list.filter(p =>
      (p.name_nl || '').toLowerCase().includes(q)
      || (p.sku || '').toLowerCase().includes(q)
      || (p.slug || '').toLowerCase().includes(q),
    ).slice(0, 50);
  })();

  const link = async () => {
    if (!stagingId) {
      setFeedback({ ok: false, msg: 'Run with "Persist to staging" enabled first.' });
      return;
    }
    if (!linkProductId) {
      setFeedback({ ok: false, msg: 'Select a product first.' });
      return;
    }
    setLinkBusy(true);
    try {
      const { data } = await adminApi.legacyStagingLink(stagingId, {
        productId: linkProductId,
        note: linkNote,
      });
      onApplied?.(['__match__'], data.stagingRow);
      setFeedback({ ok: true, msg: `Linked → ${data.stagingRow?.matchedProductName || linkProductId}` });
    } catch (e) {
      setFeedback({ ok: false, msg: e?.response?.data?.detail || e.message });
    } finally {
      setLinkBusy(false);
    }
  };

  const unlink = async () => {
    if (!stagingId) return;
    setLinkBusy(true);
    try {
      const { data } = await adminApi.legacyStagingUnlink(stagingId, {
        reason: 'unlinked from UI',
      });
      onApplied?.(['__match__'], data.stagingRow);
      setFeedback({ ok: true, msg: 'Override removed; row re-resolved.' });
    } catch (e) {
      setFeedback({ ok: false, msg: e?.response?.data?.detail || e.message });
    } finally {
      setLinkBusy(false);
    }
  };

  const loadDiagnostics = async () => {
    if (!stagingId || diagnostics || diagBusy) return;
    setDiagBusy(true);
    try {
      const { data } = await adminApi.legacyStagingDiagnostics(stagingId);
      setDiagnostics(data);
      // Pre-select the top candidate so the operator confirms-or-changes.
      if (data.candidates?.[0]) setLinkProductId(data.candidates[0].productId);
    } catch (e) {
      setFeedback({ ok: false, msg: e?.response?.data?.detail || e.message });
    } finally {
      setDiagBusy(false);
    }
  };

  const reject = async () => {
    if (!stagingId) return;
    if (!window.confirm('Reject this legacy row? It will be excluded from bulk apply.')) return;
    setLinkBusy(true);
    try {
      await adminApi.legacyStagingReject(stagingId, { reason: 'rejected from UI' });
      onApplied?.(['__match__'], { ...row, status: 'rejected' });
      setFeedback({ ok: true, msg: 'Row rejected; excluded from bulk apply.' });
    } catch (e) {
      setFeedback({ ok: false, msg: e?.response?.data?.detail || e.message });
    } finally {
      setLinkBusy(false);
    }
  };

  const createProduct = async () => {
    if (!stagingId) return;
    if (!window.confirm('Create a new INACTIVE local product from this legacy row? You must explicitly publish it later.')) return;
    setLinkBusy(true);
    try {
      const { data } = await adminApi.legacyStagingCreateProduct(stagingId, {
        allowPrice: false,  // explicit safe default
      });
      onApplied?.(['__match__'], data.stagingRow);
      setFeedback({ ok: true, msg: `Created inactive product (id=${data.productId}).` });
    } catch (e) {
      setFeedback({ ok: false, msg: e?.response?.data?.detail || e.message });
    } finally {
      setLinkBusy(false);
    }
  };

  const fields = Array.from(new Set([
    ...Object.keys(proposed), ...Object.keys(conflicts),
  ])).filter(f => FIELD_LABELS[f]);

  const toggle = (f) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(f) ? next.delete(f) : next.add(f);
      return next;
    });
  };

  const apply = async () => {
    if (!stagingId) {
      setFeedback({ ok: false, msg: 'Run with "Persist to staging" enabled first to apply.' });
      return;
    }
    if (!selected.size) {
      setFeedback({ ok: false, msg: 'Select at least one field.' });
      return;
    }
    setBusy(true);
    try {
      const { data } = await adminApi.legacyStagingApply(stagingId, {
        selectedFields: Array.from(selected),
        allowPrice,
        allowOverwrite,
      });
      const applied = data.applied || [];
      const skippedRows = (data.skipped || []).map(s =>
        typeof s === 'string' ? { field: s, reason: '' } : s);
      setFeedback({
        ok: applied.length > 0,
        msg: applied.length
          ? `Applied: ${applied.join(', ')}`
          : 'Nothing was applied.',
        skipped: skippedRows,
      });
      onApplied?.(applied);
    } catch (e) {
      setFeedback({ ok: false, msg: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`staging-card-${row.sourceSlug || row.sourceUrl}`} className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center gap-3 flex-wrap">
        <a href={row.sourceUrl} target="_blank" rel="noreferrer noopener"
          className="text-sm font-semibold text-foreground hover:underline inline-flex items-center gap-1">
          {row.scrapedData?.productName || row.sourceSlug}
          <ExternalLink className="w-3 h-3 opacity-60" />
        </a>
        <span className="text-[10px] font-mono text-muted-foreground">{row.sourceSlug}</span>
        <span className="ml-auto inline-flex items-center gap-2 text-xs">
          {row.matchedProductId ? (
            <>
              <span className="text-emerald-700 font-semibold">Matched</span>
              <span className="text-muted-foreground">→ {row.matchedProductName}</span>
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted">
                {row.matchedBy} · {Math.round((row.confidence || 0) * 100)}%
              </span>
              {isManualOverride && (
                <span data-testid={`override-badge-${row.sourceSlug}`}
                  className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 border border-indigo-200">
                  Override active
                </span>
              )}
            </>
          ) : (
            <span className="text-amber-700 font-semibold inline-flex items-center gap-1">
              <XCircle className="w-3 h-3" /> Unmatched
            </span>
          )}
        </span>
      </div>

      <div className="px-4 py-3">
        {/* Manual link / unlink for unmatched OR manually-overridden rows */}
        {(!row.matchedProductId || isManualOverride) && stagingId && (
          <div data-testid={`link-block-${row.sourceSlug}`}
            className="bg-amber-50/50 border border-amber-200 rounded-lg p-3 mb-3">
            {isManualOverride ? (
              <div className="flex items-center gap-3">
                <p className="text-xs text-indigo-900 flex-1">
                  This row is linked to <strong>{row.matchedProductName}</strong> via a manual override.
                  Removing the override will fall back to automatic matching.
                </p>
                <button data-testid={`unlink-btn-${row.sourceSlug}`}
                  onClick={unlink} disabled={linkBusy}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full border border-rose-300 text-rose-700 hover:bg-rose-50 disabled:opacity-50">
                  Remove override
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-amber-900">No automatic match. Pick the local product this scrape belongs to:</p>

                {/* Decision support — closest local candidates */}
                {!diagnostics && (
                  <button data-testid={`diag-btn-${row.sourceSlug}`}
                    onClick={loadDiagnostics} disabled={diagBusy}
                    className="text-[11px] underline text-indigo-700 hover:text-indigo-900">
                    {diagBusy ? 'Analysing…' : 'Show closest candidates'}
                  </button>
                )}
                {diagnostics && (
                  <div data-testid={`diag-${row.sourceSlug}`} className="bg-white border border-amber-200 rounded p-2 text-[11px]">
                    <p className="text-muted-foreground mb-1">
                      Tokens: <span className="font-mono">{(diagnostics.tokens || []).join(' · ') || '—'}</span>
                      {diagnostics.scrapedPrice != null && <> · scraped price: €{diagnostics.scrapedPrice}</>}
                      {diagnostics.scrapedPackage && <> · {diagnostics.scrapedPackage}</>}
                    </p>
                    <p className="font-semibold text-foreground mt-1.5 mb-0.5">Closest local products:</p>
                    {(diagnostics.candidates || []).length === 0 ? (
                      <p className="text-muted-foreground italic">No close candidates found.</p>
                    ) : (
                      <ul className="space-y-0.5">
                        {diagnostics.candidates.slice(0, 5).map(c => (
                          <li key={c.productId} className="flex items-center gap-2">
                            <input type="radio" name={`cand-${row.sourceSlug}`}
                              checked={linkProductId === c.productId}
                              onChange={() => setLinkProductId(c.productId)} />
                            <span className="font-mono text-emerald-700">{Math.round(c.score * 100)}%</span>
                            <span className="text-foreground">{c.name_nl}</span>
                            <span className="text-muted-foreground">· {c.sku || '—'}</span>
                            {!c.isActive && <span className="text-[10px] text-amber-700">(inactive)</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                    {(diagnostics.aaHints || []).length > 0 && (
                      <p className="text-muted-foreground mt-1.5">
                        AA/UTB hints: {diagnostics.aaHints.map(h => h.aaSku || h.utbSku).filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    data-testid={`link-search-${row.sourceSlug}`}
                    type="text" placeholder="Search by name / SKU / slug…"
                    value={productSearch}
                    onChange={e => setProductSearch(e.target.value)}
                    onFocus={ensureProducts}
                    className="flex-1 min-w-[260px] px-3 py-1.5 text-xs rounded border border-border bg-card focus:outline-none focus:ring-1 focus:ring-ring" />
                  <select data-testid={`link-select-${row.sourceSlug}`}
                    value={linkProductId}
                    onChange={e => setLinkProductId(e.target.value)}
                    onFocus={ensureProducts}
                    className="flex-1 min-w-[260px] px-3 py-1.5 text-xs rounded border border-border bg-card font-mono">
                    <option value="">— select a product —</option>
                    {filteredProducts.map(p => (
                      <option key={p._id} value={p._id}>
                        {p.name_nl} {p.sku ? `· ${p.sku}` : ''} {p.isActive ? '' : '(inactive)'}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input
                    data-testid={`link-note-${row.sourceSlug}`}
                    type="text" placeholder="Optional note (why this match?)"
                    value={linkNote}
                    onChange={e => setLinkNote(e.target.value)}
                    className="flex-1 min-w-[300px] px-3 py-1.5 text-xs rounded border border-border bg-card" />
                  <button data-testid={`link-btn-${row.sourceSlug}`}
                    onClick={link} disabled={linkBusy || !linkProductId}
                    className="text-xs font-semibold px-3 py-1.5 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                    Link to product
                  </button>
                </div>

                {/* Alternate actions for catalogue-gap rows */}
                <div className="flex items-center gap-2 pt-1 border-t border-amber-200/60 text-[11px]">
                  <span className="text-muted-foreground">Alternative actions:</span>
                  <button data-testid={`reject-btn-${row.sourceSlug}`}
                    onClick={reject} disabled={linkBusy}
                    className="text-rose-700 hover:text-rose-900 underline disabled:opacity-50">
                    Reject row
                  </button>
                  <span className="text-muted-foreground">·</span>
                  <button data-testid={`create-btn-${row.sourceSlug}`}
                    onClick={createProduct} disabled={linkBusy}
                    className="text-amber-700 hover:text-amber-900 underline disabled:opacity-50"
                    title="Creates a new INACTIVE local product from this legacy row. You must publish manually.">
                    Create ops-only product from legacy row
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {fields.length === 0 ? (
          <p className="text-xs text-muted-foreground">No usable fields extracted.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr>
                <th className="w-8"></th>
                <th className="text-left py-1 pr-2">Field</th>
                <th className="text-left py-1 pr-2">Existing</th>
                <th className="text-left py-1 pr-2">Scraped</th>
              </tr>
            </thead>
            <tbody>
              {fields.map(f => {
                const inProposed = f in proposed;
                const inConflict = f in conflicts;
                const existing = inConflict
                  ? conflicts[f].existing
                  : (inProposed ? '— (empty)' : '—');
                const scraped = inConflict
                  ? conflicts[f].scraped
                  : proposed[f];
                const isApplied = applied.includes(f);
                const dangerous = HIGH_RISK_FIELDS.has(f);
                return (
                  <tr key={f} className="border-t border-border align-top">
                    <td className="py-1.5 pr-2">
                      <input type="checkbox" data-testid={`field-${f}`}
                        disabled={isApplied || busy}
                        checked={selected.has(f)}
                        onChange={() => toggle(f)} />
                    </td>
                    <td className="py-1.5 pr-2 font-semibold text-foreground whitespace-nowrap">
                      {FIELD_LABELS[f]}
                      {dangerous && <span className="ml-1 text-[10px] text-amber-700">(high-risk)</span>}
                      {inConflict && <span className="ml-1 text-[10px] text-rose-700">(conflict)</span>}
                      {isApplied && <span className="ml-1 text-[10px] text-emerald-700">✓ applied</span>}
                    </td>
                    <td className="py-1.5 pr-2 text-muted-foreground whitespace-pre-wrap">
                      {formatVal(existing)}
                    </td>
                    <td className="py-1.5 pr-2 whitespace-pre-wrap">
                      {formatVal(scraped)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {row.scrapedData?.imageUrls?.length > 0 && (
          <div className="mt-3 flex gap-2 flex-wrap">
            {row.scrapedData.imageUrls.slice(0, 4).map(u => (
              <img key={u} src={u} alt="" className="w-16 h-16 object-contain bg-muted rounded border border-border" />
            ))}
          </div>
        )}

        <div className="mt-3 flex items-center gap-3 flex-wrap">
          {selected.has('price') && (
            <label className="text-xs text-amber-700 inline-flex items-center gap-1">
              <input type="checkbox" data-testid="allow-price-toggle" checked={allowPrice}
                onChange={e => setAllowPrice(e.target.checked)} />
              Allow price update
            </label>
          )}
          {Array.from(selected).some(f => f in conflicts) && (
            <label className="text-xs text-rose-700 inline-flex items-center gap-1">
              <input type="checkbox" data-testid="allow-overwrite-toggle" checked={allowOverwrite}
                onChange={e => setAllowOverwrite(e.target.checked)} />
              Allow overwrite of existing values
            </label>
          )}
          <button data-testid={`apply-btn-${row.sourceSlug}`}
            disabled={busy || !row.matchedProductId || !stagingId || selected.size === 0}
            onClick={apply}
            className="ml-auto inline-flex items-center gap-1.5 bg-foreground text-background rounded-full px-4 py-1.5 text-xs font-semibold hover:opacity-90 disabled:opacity-50">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Apply selected
          </button>
        </div>

        {feedback && (
          <div data-testid={`apply-feedback-${row.sourceSlug}`} className="mt-2 space-y-1">
            <p className={`text-xs ${feedback.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
              {feedback.msg}
            </p>
            {feedback.skipped?.length > 0 && (
              <ul className="text-[11px] text-rose-700 space-y-0.5 pl-3 list-disc">
                {feedback.skipped.map((s, i) => (
                  <li key={`${s.field}-${i}`} data-testid={`apply-skipped-${s.field}`}>
                    <span className="font-semibold">{FIELD_LABELS[s.field] || s.field}</span>
                    {s.reason ? ` — ${s.reason}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function formatVal(v) {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return v.length ? `${v.length} item(s)` : '—';
  const s = String(v);
  return s.length > 240 ? s.slice(0, 240) + '…' : s;
}
